import { useState, FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { signIn, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already logged in
  if (user) {
    const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/add-product';
    navigate(from, { replace: true });
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!email.trim() || !password) {
      setError('Lütfen tüm alanları doldurun');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await signIn(email, password);
      const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/add-product';
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const errorCode = (err as { code?: string })?.code;
      switch (errorCode) {
        case 'auth/invalid-email':
          setError('Geçersiz e-posta adresi');
          break;
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          setError('E-posta veya şifre hatalı');
          break;
        case 'auth/too-many-requests':
          setError('Çok fazla deneme. Lütfen daha sonra tekrar deneyin');
          break;
        default:
          setError('Giriş yapılamadı. Lütfen tekrar deneyin');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <Link to="/" className="login-logo">
            <img src="/logodeneme5-scaled.png" alt="Sibuz Home" className="logo-img" />
          </Link>
          <h1 className="login-title">Yönetici Girişi</h1>
          <p className="login-subtitle">Ürün yönetim paneline erişmek için giriş yapın</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="email">E-posta</label>
            <input
              type="email"
              id="email"
              className="form-input"
              placeholder="ornek@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="password">Şifre</label>
            <input
              type="password"
              id="password"
              className="form-input"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>

          {error && <div className="form-error">{error}</div>}

          <button type="submit" className="btn-login" disabled={loading}>
            {loading ? (
              <span className="btn-loading">
                <svg className="spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" strokeDasharray="60" strokeDashoffset="20" />
                </svg>
              </span>
            ) : (
              <span className="btn-text">Giriş Yap</span>
            )}
          </button>
        </form>

        <div className="login-footer">
          <Link to="/" className="back-link">← Ana Sayfaya Dön</Link>
        </div>
      </div>

      <style>{`
        .login-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ed 100%);
          padding: 20px;
        }
        
        .login-container {
          width: 100%;
          max-width: 420px;
          background: white;
          border-radius: 16px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          padding: 48px 40px;
        }
        
        .login-header {
          text-align: center;
          margin-bottom: 32px;
        }
        
        .login-logo {
          display: inline-block;
          margin-bottom: 24px;
        }
        
        .login-logo .logo-img {
          height: 40px;
          width: auto;
        }
        
        .login-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 8px 0;
        }
        
        .login-subtitle {
          font-size: 0.95rem;
          color: #666;
          margin: 0;
        }
        
        .login-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        
        .login-form .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .login-form .form-label {
          font-size: 0.9rem;
          font-weight: 600;
          color: #333;
        }
        
        .login-form .form-input {
          padding: 14px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 10px;
          font-size: 1rem;
          transition: all 0.2s ease;
          outline: none;
        }
        
        .login-form .form-input:focus {
          border-color: #c9a962;
          box-shadow: 0 0 0 3px rgba(201, 169, 98, 0.1);
        }
        
        .form-error {
          color: #dc2626;
          font-size: 0.875rem;
          min-height: 20px;
          text-align: center;
        }
        
        .btn-login {
          width: 100%;
          padding: 16px;
          background: #1a1a1a;
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        
        .btn-login:hover:not(:disabled) {
          background: #333;
          transform: translateY(-1px);
        }
        
        .btn-login:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        .btn-loading .spinner {
          width: 20px;
          height: 20px;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .login-footer {
          margin-top: 32px;
          text-align: center;
        }
        
        .back-link {
          color: #666;
          font-size: 0.9rem;
          text-decoration: none;
          transition: color 0.2s;
        }
        
        .back-link:hover {
          color: #1a1a1a;
        }
        
        @media (max-width: 480px) {
          .login-container {
            padding: 32px 24px;
          }
          
          .login-title {
            font-size: 1.3rem;
          }
        }
      `}</style>
    </div>
  );
}
